import React from "react";
import Header from "../../components/Header/Header";
import Sidebar from "./components/Sidebar/Sidebar";
import RequestList from "./RequestList/RequestList";
import Request from "./RequestDetailsPage/Request";
import ProfileModal from "./components/Modals/ProfileModal";
import Account from "./Account/AccountPage";
import BackButton from "components/ui/back-button";
import NewRequestPage from "./NewRequestPage/NewRequestPage";
import { useState, useEffect, useCallback } from "react";
// @ts-ignore
import { useIdle } from "@uidotdev/usehooks";
import baseApi from "utils/axios-instance";
import { useNavigate, useParams } from "react-router-dom";
import {
  IDLE_MS,
  DASHBOARD_UPDATE_POLLING_FREQUENCY_MS,
} from "utils/constants";
import { DEBUG_EMAILS } from "utils/constants";
import { Mixpanel } from "utils/Analytics";
import { useDispatch, useSelector } from "react-redux";
import {
  Practice,
  ProfileInfo,
  Provider,
  UserTransferPhoneNumber,
} from "../../utils/ProfileInfo";
import {
  INBOX,
  REQUEST,
  ACCOUNT,
  NEW_REQUEST_PAGE,
  PROFILEMODAL,
} from "utils/reducers";
import fetchRequest from "store/thunks/fetchRequest";
import { X, Loader2 } from "lucide-react";

function MVP() {
  const showProfileModal = useSelector((state: any) => state.profileModal.show);
  const showAccount = useSelector((state: any) => state.showAccount.show);
  const showRequest = useSelector((state: any) => state.showRequest.show);
  const showNewRequestPage = useSelector(
    (state: any) => state.showNewRequestPage.show
  );

  const [loading, setLoading] = useState(true);
  const [inboxCount, setInboxCount] = useState(0);
  const [currentUser, setCurrentUser] = useState({
    email: "",
    first_name: "",
    last_name: "",
    picture_url: "",
    id: "",
    team_id: "",
  });
  const [isTeamInbox, setIsTeamInbox] = useState(false);
  const [requestRows, setRequestRows] = useState([]);
  const [debugUser, setDebugUser] = useState(false);
  const [profileInfo, setProfileInfo] = useState<ProfileInfo>();
  const [isMobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // Deeplink functionality to the underlying request page.
  const { inquiryId } = useParams();
  const dispatch = useDispatch();
  useEffect(() => {
    if (inquiryId !== undefined) {
      dispatch({ type: REQUEST.display });
      dispatch(fetchRequest(inquiryId) as any);
    }
  }, [dispatch, inquiryId]);

  const navigate = useNavigate();

  const logout = useCallback(() => {
    baseApi
      .get("/logout")
      .then((response) => {
        window.location.replace("/"); // Full page reload
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  const idle = useIdle(IDLE_MS);

  // Logout when idle.
  useEffect(() => {
    if (currentUser.id === "") {
      return;
    }
    if (idle) {
      if (debugUser) {
        console.log("Debug user detected. Bypassing idle logout.");
        return;
      }
      Mixpanel.track("Idle Logout", currentUser);
      logout();
    }
  }, [idle, currentUser, debugUser, logout]);

  const reloadPage = async (
    showLoadingOverlay: boolean = true,
    inboxType?: string
  ) => {
    if (showLoadingOverlay) {
      setLoading(true);
    }

    let dataRoute = "";
    const targetInboxType =
      inboxType || (isTeamInbox ? INBOX.team_inbox : INBOX.current_user_inbox);

    if (targetInboxType === INBOX.current_user_inbox)
      dataRoute = "/dashboard/data/current_user";
    if (targetInboxType === INBOX.team_inbox) dataRoute = "/dashboard/data";

    try {
      const response = await baseApi.get(dataRoute, { withCredentials: true });
      if (JSON.stringify(requestRows) !== JSON.stringify(response.data)) {
        setRequestRows(response.data);
        setInboxCount(response.data.length);
      }
    } catch (error) {
      console.log(error);
      Mixpanel.track("Error: Reload Page", { error: error });
    } finally {
      if (showLoadingOverlay) {
        setLoading(false);
      }
    }
  };

  const refreshProfileInfo = useCallback(() => {
    return baseApi
      .get("/profile")
      .then((response) => {
        // TODO(alan): Verification of result from backend
        var practices: Array<Practice> = [];
        for (const practice of response.data.practices) {
          var providers: Array<Provider> = [];
          for (const provider of practice.providers) {
            providers.push({
              name: provider.name,
              npi: provider.npi,
              id: provider.id,
            });
          }
          practices.push({
            name: practice.name,
            tax_id: practice.tax_id,
            providers: providers,
            address: practice.address,
            city: practice.city,
            state: practice.state,
            zip: practice.zip,
            id: practice.id,
          });
        }
        var user_transfer_phone_numbers: Array<UserTransferPhoneNumber> = [];
        for (const phone_number of response.data.phone_numbers) {
          user_transfer_phone_numbers.push({
            name: phone_number.name,
            phoneNumber: phone_number.phone_number,
          });
        }
        var latest_profile_info: ProfileInfo = {
          specialty: response.data.specialty,
          practices: practices,
          phoneNumbers: user_transfer_phone_numbers,
          is_copilot: response.data.is_copilot,
        };
        setProfileInfo(latest_profile_info);
      })
      .catch((error) => {
        Mixpanel.track("Error: Retrieving Latest Profile Info", {
          error: error,
        });
        console.log(error);
      });
  }, []);

  const fetchAllRequests = useCallback(
    (showLoading: boolean = true, isTeamInbox: boolean = false) => {
      if (showLoading) {
        setLoading(true);
      }
      let dataRoute: string = "";
      if (isTeamInbox) {
        dataRoute = "/dashboard/data";
      } else {
        dataRoute = "/dashboard/data/current_user";
      }
      return baseApi
        .get(dataRoute, { withCredentials: true })
        .then((response) => {
          setRequestRows(response.data);
          setInboxCount(response.data.length);
        })
        .catch((error) => {
          console.log(error);
          Mixpanel.track("Error: Fetch Initial Requests", { error: error });
        })
        .finally(() => {
          if (showLoading) {
            setLoading(false);
          }
        });
    },
    []
  );

  const searchPatients = useCallback(
    async (query: string) => {
      if (query === "" || query === undefined) {
        setLoading(false);
        setIsSearching(false);
        return;
      }

      setLoading(true);
      setIsSearching(true);
      try {
        const response = await baseApi.get("/dashboard/search", {
          params: { query: query },
          withCredentials: true,
        });

        if (JSON.stringify(requestRows) !== JSON.stringify(response.data)) {
          setRequestRows(response.data);
          setInboxCount(response.data.search_results);
        }
      } catch (error) {
        console.log(error);
        Mixpanel.track("Error: Search Patients", { error: error });
      } finally {
        setLoading(false);
      }
    },
    [requestRows]
  );

  const handleClearSearch = useCallback(() => {
    // Reload the normal inbox data when search is cleared
    setIsSearching(false);
    fetchAllRequests(true, isTeamInbox);
  }, [fetchAllRequests, isTeamInbox]);

  //fetch data when inbox is changed
  useEffect(() => {
    fetchAllRequests(true, isTeamInbox);
  }, [isTeamInbox, fetchAllRequests]);

  // Fetch all requests every 5 minutes when not idle, don't show loading indicator for background fetches.
  useEffect(() => {
    if (idle) {
      return;
    }
    const interval = setInterval(() => {
      // Don't show the loading indicator for background fetches
      fetchAllRequests(false, isTeamInbox);
    }, DASHBOARD_UPDATE_POLLING_FREQUENCY_MS); // 5 minutes
    return () => clearInterval(interval);
  }, [idle, fetchAllRequests, isTeamInbox]);

  // On first load, make sure we populate the lastRequest and any profile information (such as specialty, practices and providers.)
  useEffect(() => {
    const fetchInitialData = async () => {
      const userPromise = baseApi
        .get("/dashboard/current-user")
        .then((response) => {
          setCurrentUser({
            email: response.data.email,
            first_name: response.data.first_name,
            last_name: response.data.last_name,
            picture_url: response.data.picture_url,
            id: response.data.id,
            team_id: response.data.team_id,
          });

          if (DEBUG_EMAILS.includes(response.data.email)) {
            setDebugUser(true);
          }
          Mixpanel.identify(response.data.id);
          Mixpanel.people.set({
            $first_name: response.data.first_name,
            $last_name: response.data.last_name,
            $email: response.data.email,
          });
        })
        .catch((error) => {
          if (error === "401 Unauthorized") {
            navigate("/", { replace: true });
          }
          console.log(error);
          Mixpanel.track("Error: Current User", { error: error });
          logout();
        });

      await Promise.all([
        userPromise,
        refreshProfileInfo(),
        fetchAllRequests(),
      ]);
      setLoading(false);
    };
    fetchInitialData();
  }, [logout, navigate, refreshProfileInfo, fetchAllRequests]);

  // Function to render the main content based on current state
  const renderMainContent = () => {
    if (showAccount) {
      return (
        <div className="overflow-y-auto">
          <div className="border-b-2 border-gray-100 px-6 py-2 sticky top-0 bg-white z-10">
            <BackButton
              onClick={() => {
                dispatch({ type: ACCOUNT.hide });
              }}
            />
          </div>
          <div className="flex-1 overflow-y-auto">
            <Account refreshProfileInfo={refreshProfileInfo} />
          </div>
        </div>
      );
    }

    if (showNewRequestPage) {
      return (
        <div className="overflow-y-auto">
          <div className="border-b-2 border-gray-100 px-6 py-2 sticky top-0 bg-white z-10">
            <BackButton
              onClick={() => {
                dispatch({ type: NEW_REQUEST_PAGE.hide });
              }}
            />
          </div>
          <div className="flex-1 overflow-y-auto">
            <NewRequestPage
              reloadPage={(showLoading = true) => reloadPage(showLoading)}
              debugUser={debugUser}
              profileInfo={profileInfo}
              currentUser={currentUser}
            />
          </div>
        </div>
      );
    }

    if (showRequest) {
      return (
        <div className="overflow-y-auto">
          <div className="border-b-2 border-gray-100 px-6 py-2 sticky top-0 bg-white z-10">
            <BackButton
              onClick={() => {
                dispatch({ type: REQUEST.hide });
              }}
            />
          </div>
          <div className="flex-1 overflow-y-auto">
            <Request />
          </div>
        </div>
      );
    }

    // Default to RequestList
    return (
      <div className="overflow-y-auto">
        <RequestList
          inboxCount={inboxCount}
          requestRows={requestRows}
          reloadPage={(showLoading = true) =>
            fetchAllRequests(showLoading, isTeamInbox)
          }
          isTeamInbox={isTeamInbox}
        />
      </div>
    );
  };

  return (
    <>
      {/* Modals */}
      {showProfileModal && (
        <ProfileModal
          logout={logout}
          currentUser={currentUser}
          onClose={() => dispatch({ type: PROFILEMODAL.hide })}
        />
      )}

      {/* Loading Overlay - Blocks all interactions */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 shadow-xl flex flex-col items-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <p className="text-gray-700 font-medium">Loading...</p>
          </div>
        </div>
      )}

      {/* Main Layout */}
      <div
        className={`flex flex-col h-screen bg-gray-100 font-sans ${
          loading ? "pointer-events-none opacity-75" : ""
        }`}
      >
        {/* Header */}
        <div className="flex-shrink-0">
          <Header
            currentUser={currentUser}
            noSearch={showAccount || showRequest || showNewRequestPage}
            onMenuClick={() => setMobileSidebarOpen(true)}
            searchPatients={searchPatients}
            onClearSearch={handleClearSearch}
          />
        </div>

        {/* Page Body */}
        <div className="flex flex-1 overflow-hidden">
          {/* Mobile Sidebar */}
          {isMobileSidebarOpen && (
            <div className="md:hidden fixed inset-0 flex z-40">
              {/* Overlay */}
              <div
                className="fixed inset-0"
                onClick={() => setMobileSidebarOpen(false)}
              >
                <div className="absolute inset-0 bg-gray-600 opacity-75"></div>
              </div>

              {/* Sidebar Panel */}
              <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
                <div className="absolute top-0 right-0 -mr-12 pt-2">
                  <button
                    onClick={() => setMobileSidebarOpen(false)}
                    className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                  >
                    <span className="sr-only">Close sidebar</span>
                    <X className="h-6 w-6 text-white" />
                  </button>
                </div>
                <Sidebar
                  inboxCount={inboxCount}
                  isTeamInbox={isTeamInbox}
                  setIsTeamInbox={setIsTeamInbox}
                  isSearching={isSearching}
                  setIsSearching={setIsSearching}
                />
              </div>
              <div className="flex-shrink-0 w-14" aria-hidden="true">
                {/* Dummy element to force sidebar to shrink to fit close icon */}
              </div>
            </div>
          )}

          {/* Desktop Sidebar */}
          <div className="hidden md:flex md:w-64 md:flex-shrink-0">
            <div className="flex flex-col w-64">
              <Sidebar
                inboxCount={inboxCount}
                isTeamInbox={isTeamInbox}
                setIsTeamInbox={setIsTeamInbox}
                isSearching={isSearching}
                setIsSearching={setIsSearching}
              />
            </div>
          </div>

          {/* Main Content Area */}
          <main className="flex-1 overflow-hidden bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col m-2 mt-0">
            {renderMainContent()}
          </main>
        </div>
      </div>
    </>
  );
}

export default MVP;
